import React, { useState, useEffect } from 'react';
import './MoreClassSelection.scss';
import img from '../../../assets/img/1.svg';
import { useDispatch, useSelector } from 'react-redux';
import {
  Typography,
  Box,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  setSelectedCourses,
  clearEndorsements,
} from '../../../redux/actions/WizardLaunchActions';

function MoreClassSelection({ setFormState }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const dynamicData = useSelector(
    (state) => state.wizardLaunch.screenData.multi_select_courses
  );
  const isLoading = useSelector(
    (state) => state.wizardLaunch.dynamicDataLoading
  );
  const error = useSelector((state) => state.wizardLaunch.dynamicDataError);
  const moreClassSelection = useSelector(
    (state) => state.wizardLaunch.moreClassSelection
  );

  const [totalPrice, setTotalPrice] = useState(0);
  const [basePrice, setBasePrice] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // Clear endorsements when component mounts
  useEffect(() => {
    dispatch(clearEndorsements());
  }, [dispatch]);

  // Hidden endorsements effect
  useEffect(() => {
    if (dynamicData?.options && 
        !moreClassSelection?.some(item => item.hiddenInEndorsements === "1")) {
      const hiddenCourses = dynamicData.options.filter(
        option => option.hidden_in_endorsements === "1"
      );

      if (hiddenCourses.length > 0) {
        const hiddenCoursesData = hiddenCourses.map(course => ({
          id: course.question_answer_id,
          questionAnswerId: course.question_answer_id,
          hiddenInEndorsements: course.hidden_in_endorsements,
          courseType: course.answer.CourseType[1].name,
          price: parseFloat(course.answer.CourseType[1].price),
          discount: 0,
          name: course.answer.name,
          imgUrl: course.answer.imgUrl,
          section_id: course.section_id
        }));

        // Preserve existing selections while adding hidden courses
        const updatedSelection = [...(moreClassSelection || []), ...hiddenCoursesData];
        dispatch(setSelectedCourses(updatedSelection));
      }
    }
  }, [dynamicData, dispatch]);

  // Calculate prices effect
  useEffect(() => {
    if (moreClassSelection && moreClassSelection.length > 0 && dynamicData) {
      calculateTotalPrice(moreClassSelection);
    }
  }, [moreClassSelection, dynamicData]);

  // Fixed calculateTotalPrice function
  const calculateTotalPrice = (selected) => {
    if (!selected || !dynamicData) return;

    // Filter out hidden courses
    const visibleCourses = selected.filter(course => {
      const courseOption = dynamicData.options?.find(
        option => option.question_answer_id === course.questionAnswerId
      );
      return courseOption?.hidden_in_endorsements === "0";
    });

    // Calculate base price only for visible courses
    const calculatedBasePrice = visibleCourses.reduce(
      (total, course) => total + (course.price || 0),
      0
    );

    // Calculate course discounts only for visible courses
    const courseDiscounts = visibleCourses.reduce(
      (total, course) => total + (course.discount || 0),
      0
    );

    // Calculate multiple selection discount only for visible courses
    const offerValue = parseFloat(dynamicData?.offer?.replace('$', '') || 0);
    const multiSelectionDiscount =
      visibleCourses.length > 1 ? (visibleCourses.length - 1) * offerValue : 0;

    // Total discount is the sum of course discounts and multiple selection discount
    const totalDiscount = courseDiscounts + multiSelectionDiscount;

    // Calculate final price
    const finalPrice = calculatedBasePrice - totalDiscount;

    // Update state with calculated values
    setBasePrice(calculatedBasePrice);
    setDiscount(totalDiscount);
    setTotalPrice(finalPrice);
  };

  const handleCourseSelect = async (
    questionAnswerId,
    courseType,
    price,
    courseDiscount,
    imgUrl
  ) => {
    setIsProcessing(true);
    try {
      let updatedSelected = [...moreClassSelection];
      
      // Check if the course is already selected
      const isAlreadySelected = updatedSelected.some(
        course => course.questionAnswerId === questionAnswerId && course.courseType === courseType
      );

      if (isAlreadySelected) {
        // If already selected, remove it (deselect)
        updatedSelected = updatedSelected.filter(
          course => !(course.questionAnswerId === questionAnswerId && course.courseType === courseType)
        );
      } else {
        // Remove any existing selection for this questionAnswerId
        updatedSelected = updatedSelected.filter(
          course => course.questionAnswerId !== questionAnswerId
        );

        // Add the newly selected courseType
        const courseOption = dynamicData?.options?.find(
          option => option.question_answer_id === questionAnswerId
        );

        const mappedItem = {
          id: questionAnswerId,
          questionAnswerId,
          courseType,
          price,
          discount: courseDiscount,
          name: courseOption?.answer.name,
          imgUrl,
        };
        if (courseOption?.section_id) {
          mappedItem.section_id = courseOption?.section_id;
        }
        updatedSelected.push(mappedItem);
      }

      dispatch(setSelectedCourses(updatedSelected));
      calculateTotalPrice(updatedSelected);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRedirect = () => {
    if (moreClassSelection && moreClassSelection.length === 0) {
      setOpenDialog(true);
      return;
    }

    const hasSelfPaced = moreClassSelection?.some(
      (course) => course.courseType === 'Self-Paced'
    );
    const hasInstructorLed = moreClassSelection?.some(
      (course) => course.courseType === 'Instructor-Led'
    );

    if (hasSelfPaced && hasInstructorLed) {
      navigate('/shoppingcart');
    } else if (hasSelfPaced) {
      navigate('/shoppingcart');
    } else if (hasInstructorLed) {
      navigate('/contact');
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  if (isLoading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='200px'>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='200px'>
        <Typography color='error'>
          Error loading Course section, Please try again later.
        </Typography>
      </Box>
    );
  }

  return (
    <div className='more-class-endorsements-wrap'>
      <Typography variant='h5'>{dynamicData?.question_text}</Typography>
      <Typography className='lbl-text'>
        You can select 1 or more options
      </Typography>
      <div className='card-wrap'>
        {dynamicData?.options
          ?.filter(option => option.hidden_in_endorsements === "0")
          .map((option) => (
          <div
            key={option.question_answer_id}
            className={`cards ${
              moreClassSelection?.some(
                (course) =>
                  course.questionAnswerId === option.question_answer_id
              )
                ? 'selected'
                : ''
            }`}
          >
            {/* <img src={img} alt="img" /> */}
            <img src={option.answer.imgUrl} alt='img' />
            <div className='heading-sec'>
              <Typography className='title'>{option.answer.name}</Typography>
              <Typography 
                className='description'>
                {option.answer.description}
              </Typography>
            </div>
           <div style={{display: 'flex', flexDirection: 'column', minHeight: '110px'}}>
             {/* First position - Instructor-Led */}
             <div className='inst-lead-wrap' style={{ visibility: option.answer.CourseType.find(type => type.name === 'Instructor-Led') ? 'visible' : 'hidden' }}>
               {option.answer.CourseType.find(type => type.name === 'Instructor-Led') ? (
                 <>
                   <div className='lbl'>Instructor-Led</div>
                   <Button
                     className={moreClassSelection?.some(
                       course => course.questionAnswerId === option.question_answer_id && 
                               course.courseType === 'Instructor-Led'
                     ) ? 'btn-added' : 'btn-add'}
                     onClick={() =>
                       handleCourseSelect(
                         option.question_answer_id,
                         'Instructor-Led',
                         parseFloat(option.answer.CourseType.find(type => type.name === 'Instructor-Led').price),
                         parseFloat(option.answer.CourseType.find(type => type.name === 'Instructor-Led').discount || 0),
                         option.answer.imgUrl
                       )
                     }
                     disabled={isProcessing}
                   >
                     {isProcessing ? (
                       <CircularProgress size={20} />
                     ) : moreClassSelection?.some(
                       course => course.questionAnswerId === option.question_answer_id && 
                               course.courseType === 'Instructor-Led'
                     ) ? (
                       'Added'
                     ) : (
                       `$${option.answer.CourseType.find(type => type.name === 'Instructor-Led').price}`
                     )}
                   </Button>
                 </>
               ) : <div style={{ height: '37px' }}></div>}
             </div>
             
             {/* Second position - Self-Paced */}
             <div className='inst-lead-wrap' style={{ visibility: option.answer.CourseType.find(type => type.name === 'Self-Paced') ? 'visible' : 'hidden' }}>
               {option.answer.CourseType.find(type => type.name === 'Self-Paced') ? (
                 <>
                   <div className='lbl'>Self-Paced</div>
                   <Button
                     className={moreClassSelection?.some(
                       course => course.questionAnswerId === option.question_answer_id && 
                               course.courseType === 'Self-Paced'
                     ) ? 'btn-added' : 'btn-add'}
                     onClick={() =>
                       handleCourseSelect(
                         option.question_answer_id,
                         'Self-Paced',
                         parseFloat(option.answer.CourseType.find(type => type.name === 'Self-Paced').price),
                         parseFloat(option.answer.CourseType.find(type => type.name === 'Self-Paced').discount || 0),
                         option.answer.imgUrl
                       )
                     }
                     disabled={isProcessing}
                   >
                     {isProcessing ? (
                       <CircularProgress size={20} />
                     ) : moreClassSelection?.some(
                       course => course.questionAnswerId === option.question_answer_id && 
                               course.courseType === 'Self-Paced'
                     ) ? (
                       'Added'
                     ) : (
                       `$${option.answer.CourseType.find(type => type.name === 'Self-Paced').price}`
                     )}
                   </Button>
                 </>
               ) : <div style={{ height: '37px' }}></div>}
             </div>
           </div>
          </div>
        ))}
      </div>
      <div className='discount-wrap'>
        <Typography variant='h4'>Multiple Selection Discount!</Typography>
        <Typography>
          Save {dynamicData?.offer} on each additional selection when choosing
          multiple endorsements.
        </Typography>
      </div>
      <div className='priceSummary-sec'>
        <div className='priceSummary-wrap'>
          <Typography>
            <strong>Price Summary</strong>
          </Typography>
          <Typography className='border-bottom'>
            Selected endorsements:{' '}
            <span>
              <strong>
                {
                  new Set(
                    (moreClassSelection || []).filter(course => {
                        const courseOption = dynamicData.options?.find(
                          option => option.question_answer_id === course.questionAnswerId
                        );
                        return courseOption?.hidden_in_endorsements === "0";
                      })
                      .map((course) => course.questionAnswerId)
                  ).size
                }
              </strong>
            </span>
          </Typography>
          <Typography className='border-bottom'>
            Base price:{' '}
            <span>
              <strong>${basePrice?.toFixed(2)}</strong>
            </span>
          </Typography>
          <Typography>
            Discount:{' '}
            <span>
              <strong>${discount?.toFixed(2)}</strong>
            </span>
          </Typography>
          <Typography className='total-txt'>
            <strong>Total: ${totalPrice?.toFixed(2)}</strong>
          </Typography>
        </div>
      </div>
      <div className='btn-wrap'>
        <Button className='btn-dark' onClick={handleRedirect}>
          Go to Pay
        </Button>
      </div>

      {/* Dialog for no course selected */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>No Endorsement Selected</DialogTitle>
        <DialogContent>
          <Typography>
            Please select at least one course before proceeding to pay.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color='primary'>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default MoreClassSelection;
